import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import Header from 'components/headers/home'
import SideBar from 'components/sidebar'
import SideBarItems from 'components/sidebar/items'
import AccountNumber from 'assets/images/home/AccountNumber.svg'
import Headphone from 'assets/images/home/Headphone.svg'
import Wallet from 'assets/images/home/Wallet.svg'
import HowToStart from 'assets/images/home/HowToStart.svg'
import RulesAndConditions from 'assets/images/home/RulesAndConditions.svg'
import Arrow from 'assets/images/home/Arrow.svg'
import SupportModal from 'components/modals/supportModal'
import { getBoxes } from 'services/funds'
import { getMyGuarantees } from 'services/guarantees'
import { PulseCard, SectionError } from 'components/states'
import PayablesCard from './PayablesCard'
import GuaranteesCard from './GuaranteesCard'
import DepositsCard from './DepositsCard'
import ManagedFunds from './ManagedFunds'
import AddToHomeScreenPrompt from 'components/miniapp/addToHomeScreen'
import usePayables from 'modules/easyPay/hooks/usePayables'

/**
 * The home page.
 *
 * What this replaced: 312 lines of static marketing with no data fetching at
 * all — a hero banner, a weekday strip whose event dot was hard-coded
 * `invisible`, a coaching banner whose CTA only raised «این صفحه در حال توسعه
 * است», and four outbound links to the marketing site. It could not tell you
 * whether you owed three million toman or nothing.
 *
 * The structure is fixed and never reorders: پرداختنی‌ها, then ضمانت‌ها, then
 * سرمایهٔ شما, then مدیریت صندوق, then shortcuts. Sections with nothing to say
 * disappear rather than render an empty state, with two exceptions that are
 * genuinely informative: "you owe nothing right now", and the zero-fund first
 * run.
 *
 * سرمایه sits after the two liabilities and before the manager section because
 * the personal sections run debts-then-assets, and مدیریت صندوق is the only
 * one that is not about the member as a person.
 *
 * The one framing rule that keeps a composite user coherent: پرداختنی‌ها is
 * always *you as a person*, مدیریت صندوق is always *the fund as an
 * institution*. A manager's own overdue installment appears in the first and
 * never in the second.
 *
 * Reuses the `['get-boxes']` query key rather than `useGetFunds`, which fires
 * a `funds_list_viewed` PostHog event in a useEffect and would double-count
 * the funds-tab funnel on every home render.
 */
export default function HomePage() {
    const [openSupport, setOpenSupport] = useState<boolean>(false)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const {
        data: boxes,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery(['get-boxes'], getBoxes, { staleTime: 60_000 })

    // Both of these share their query key with the card that renders them
    // (`['get-all-payment']`, `['my-guarantees']`), so reading the numbers up
    // here for the landing event costs no additional request.
    const { summary: payables, isLoading: payablesLoading } = usePayables()
    const { data: guarantees, isLoading: guaranteesLoading } = useQuery(
        ['my-guarantees'],
        getMyGuarantees,
        { staleTime: 60_000 }
    )

    const moneyPools = boxes?.moneypools ?? []
    const cashBoxes = boxes?.cashboxes ?? []
    const ready = !isLoading && !isError
    const hasNoFunds = ready && moneyPools.length === 0 && cashBoxes.length === 0

    // Deliberately waits for all three, not just the box list: a `home_viewed`
    // fired the moment the funds arrive reports «۰ پرداختنی» for everyone whose
    // payables were still in flight, which is most people.
    const settled = ready && !payablesLoading && !guaranteesLoading

    useEffect(() => {
        if (!settled) return
        posthog.capture('home_viewed', {
            funds_count: moneyPools.length + cashBoxes.length,
            managed_funds_count: moneyPools.filter((mp) =>
                ['own', 'mgr'].includes(mp.poolship?.role)
            ).length,
            // The last step of a login and a tap on the home tab produce the
            // same event otherwise, so "does landing here work" is unanswerable.
            // `POST_LOGIN_PATH` leaves the marker on the URL.
            entry:
                searchParams.get('src') === 'login' ? 'post_login' : 'in_app',
            // What the member actually landed on. A home page with nothing owed
            // and one with three overdue installments are different screens and
            // should not aggregate into one funnel step.
            overdue_count: payables.overdueCount,
            payables_total: payables.grandTotal,
            has_guarantees: !!guarantees?.summary?.active_count,
        })
        // Keyed on the fetched data, not on every re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settled, boxes])

    // A landing page that failed to load produced no event at all, so it was
    // indistinguishable from an app nobody opened.
    useEffect(() => {
        if (!isError) return
        const axiosError = error as {
            code?: string
            response?: { status?: number }
            config?: { url?: string }
        }
        posthog.capture('home_load_failed', {
            status: axiosError?.response?.status ?? null,
            // `status` alone was null on 524 of 525 failures — i.e. no HTTP
            // response came back at all — and that is where the trail went
            // cold. These three say WHICH kind of nothing: a dropped
            // connection, a client-side abort, or a device that was simply
            // offline. Without them "the home page failed for 379 people"
            // is unactionable.
            //
            // `code` is axios's own enum (ERR_NETWORK, ECONNABORTED,
            // ERR_CANCELED …), never the raw message — that can carry a URL
            // with query parameters in it.
            error_code: axiosError?.code ?? null,
            endpoint: axiosError?.config?.url ?? null,
            online:
                typeof navigator === 'undefined' ? null : navigator.onLine,
        })
    }, [isError, error])

    const firstName = (() => {
        try {
            const raw = localStorage.getItem('user')
            const name = raw ? JSON.parse(raw)?.name : ''
            return typeof name === 'string' ? name.trim().split(' ')[0] : ''
        } catch {
            return ''
        }
    })()

    return (
        <div className="bg-canvas min-h-screen pb-6">
            <Header title={firstName ? `سلام، ${firstName}` : 'همیان'} />
            <SideBar>
                <SideBarItems />
            </SideBar>

            {/* Renders nothing unless we are in a mini app, the user just
                logged in, and the client says we are installable but not yet
                installed. */}
            <AddToHomeScreenPrompt />

            <div className="px-3 pt-4 flex flex-col gap-4">
                {isLoading && <PulseCard />}
                {isError && <SectionError onRetry={() => refetch()} />}

                {hasNoFunds && (
                    <ZeroFundState
                        onCreate={() => navigate('/add-box')}
                    />
                )}

                {ready && !hasNoFunds && (
                    <>
                        <PayablesCard />
                        <GuaranteesCard />
                        <DepositsCard />
                        <ManagedFunds moneyPools={moneyPools} />
                    </>
                )}

                <Shortcuts
                    onSupport={() => setOpenSupport(true)}
                    navigate={navigate}
                />

                {hasNoFunds && <LearnMore />}
            </div>

            <SupportModal
                show={openSupport}
                onHide={() => setOpenSupport(false)}
            />
        </div>
    )
}

/**
 * Four tiles, all of which work.
 *
 * The old grid had six; «کد معرف» and a تقویم placeholder only raised
 * «این صفحه در حال توسعه است», which teaches people that tapping things on
 * this screen does nothing.
 */
function Shortcuts({
    onSupport,
    navigate,
}: {
    onSupport: () => void
    navigate: (to: string) => void
}) {
    const tiles: Array<{
        /** stable, latin, safe to break down by — the label is Persian copy */
        tile: string
        label: string
        emoji?: string
        img?: string
        onClick: () => void
    }> = [
        {
            tile: 'guarantees',
            label: 'ضمانت‌های من',
            emoji: '🤝',
            onClick: () => {
                // The guarantees card fires the same event with
                // `source: 'home'`; this tile is a second door that was never
                // instrumented, so every count of «who opens ضمانت‌ها» was low.
                posthog.capture('guarantees_opened', {
                    source: 'home_shortcut',
                })
                navigate('/guarantees')
            },
        },
        {
            tile: 'wallet',
            label: 'کیف پول',
            img: Wallet,
            onClick: () => navigate('/wallet'),
        },
        {
            tile: 'bank_cards',
            label: 'کارت‌های بانکی',
            img: AccountNumber,
            onClick: () => navigate('/manage-bank-cards'),
        },
        {
            tile: 'support',
            label: 'پشتیبانی',
            img: Headphone,
            onClick: onSupport,
        },
    ]
    return (
        <div className="grid grid-cols-4 gap-2">
            {tiles.map((t) => (
                <button
                    type="button"
                    key={t.label}
                    onClick={() => {
                        posthog.capture('home_shortcut_clicked', {
                            tile: t.tile,
                        })
                        t.onClick()
                    }}
                    className="bg-white border border-divider rounded-card py-3 flex flex-col items-center gap-1 cursor-pointer"
                >
                    <span className="w-8 h-8 flex items-center justify-center text-xl">
                        {t.img ? (
                            <img
                                src={t.img}
                                alt=""
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            t.emoji
                        )}
                    </span>
                    <span className="text-[11px] text-text-body text-center leading-tight">
                        {t.label}
                    </span>
                </button>
            ))}
        </div>
    )
}

/**
 * First run. The only state where marketing content earns its place — someone
 * with no fund has nothing to triage, and the real question is "what is this
 * and how do I start".
 */
function ZeroFundState({ onCreate }: { onCreate: () => void }) {
    return (
        <div className="bg-white border border-divider rounded-card p-5 text-center">
            <p className="text-base font-bold text-heading">
                هنوز عضو هیچ صندوقی نیستید
            </p>
            <p className="mt-2 text-sm text-text-secondary leading-relaxed">
                صندوق قرض‌الحسنه‌ی خودتان را بسازید یا با لینک دعوت به صندوق
                دوستان‌تان بپیوندید.
            </p>
            <button
                type="button"
                onClick={() => {
                    posthog.capture('fund_create_started', {
                        source: 'home_empty',
                    })
                    onCreate()
                }}
                className="mt-4 w-full bg-green-1 text-white rounded-full py-2 text-sm font-bold cursor-pointer"
            >
                ساخت صندوق
            </button>
            <p className="mt-3 text-xs text-text-muted leading-relaxed">
                برای پیوستن به صندوقی که از قبل هست، از مدیر آن بخواهید لینک
                دعوت را برایتان بفرستد.
            </p>
        </div>
    )
}

/** Only rendered on first run, where there is nothing more useful to show. */
function LearnMore() {
    const rows = [
        {
            label: 'از کجا شروع کنم؟',
            img: HowToStart,
            href: 'https://hamyanapp.com/how-to-use/',
        },
        {
            label: 'قوانین و مقررات',
            img: RulesAndConditions,
            href: 'https://hamyanapp.com/policy/',
        },
    ]
    return (
        <div className="bg-white border border-divider rounded-card overflow-hidden">
            {rows.map((r, i) => (
                <a
                    key={r.label}
                    href={r.href}
                    className={`px-4 py-3 flex items-center gap-2 ${
                        i > 0 ? 'border-t border-divider' : ''
                    }`}
                >
                    <img src={Arrow} alt="" className="w-4 h-4" />
                    <span className="flex-1 text-right text-sm text-text-body">
                        {r.label}
                    </span>
                    <img src={r.img} alt="" className="w-8 h-8" />
                </a>
            ))}
        </div>
    )
}
